/* "New", from anywhere.
 *
 * Every calendar people already use has one button that makes something, and
 * it is the first thing a newcomer looks for. Amon had four ways to add — a
 * quick-add on the day, a "put something at a time" dialog, a form on Tasks,
 * a form on Hobbies — each on its own screen, so the answer to "how do I add
 * my dentist appointment" depended on already knowing which screen to be on.
 *
 * This is the one door. It does not replace those forms; it leads to them,
 * with the words for what each is ("At a time", "A task"), so choosing is
 * reading a short list rather than knowing the app.
 *
 * A real menu: `aria-haspopup`, `role="menu"`, arrow keys between items,
 * Escape to close, and focus back on the button afterwards.
 */

import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import Icon from './Icon';
import type { IconName } from './Icon';
import AddToCalendar from './AddToCalendar';
import AskAmon from './AskAmon';
import { useSession, localToday } from '../session';
import { useToast } from './toast';
import { friendlyDate } from './time';

interface Item { key: string; icon: IconName; label: string; hint: string }

const ITEMS: Item[] = [
  { key: 'ask', icon: 'sparkle', label: 'Ask Amon', hint: 'Say it in a sentence — nothing changes until you confirm' },
  { key: 'time', icon: 'clock', label: 'At a time', hint: 'An appointment, a birthday, a weekly class' },
  { key: 'task', icon: 'tasks', label: 'A task', hint: 'Something you owe — Amon finds the hours' },
  { key: 'hobby', icon: 'hobbies', label: 'A hobby', hint: 'Time held back every week' },
  { key: 'people', icon: 'people', label: 'A time with people', hint: 'A poll of times that can happen' }
];

export default function CreateMenu({ variant }: { variant: 'sidebar' | 'fab' }) {
  const [open, setOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [asking, setAsking] = useState(false);
  /* On a phone the floating button sits over whatever you are reading. It
     steps aside while you scroll down through the day and comes straight back
     the moment you scroll up — which is when you are looking for it. */
  const [away, setAway] = useState(false);
  useEffect(() => {
    if (variant !== 'fab') return;
    let last = window.scrollY;
    const onScroll = () => {
      const y = window.scrollY;
      if (Math.abs(y - last) < 12) return;
      setAway(y > last && y > 120);
      last = y;
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [variant]);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const { user, today } = useSession();
  const date = today ?? localToday(user?.tz);
  const button = useRef<HTMLButtonElement | null>(null);
  const menu = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    menu.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
    const away = (e: MouseEvent) => {
      if (!menu.current?.contains(e.target as Node) && !button.current?.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const close = () => { setOpen(false); button.current?.focus(); };

  function choose(key: string) {
    setOpen(false);
    if (key === 'ask') setAsking(true);
    if (key === 'time') setPlacing(true);
    if (key === 'task') navigate('/tasks?add=1');
    if (key === 'hobby') navigate('/hobbies');
    if (key === 'people') navigate('/people');
  }

  function onKeyDown(e: React.KeyboardEvent) {
    const items = [...(menu.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])];
    const i = items.indexOf(document.activeElement as HTMLElement);
    if (e.key === 'Escape') { e.preventDefault(); close(); }
    if (e.key === 'ArrowDown') { e.preventDefault(); items[(i + 1) % items.length]?.focus(); }
    if (e.key === 'ArrowUp') { e.preventDefault(); items[(i - 1 + items.length) % items.length]?.focus(); }
    if (e.key === 'Tab') setOpen(false);
  }

  return (
    /* `create--fab`, not `create-fab`: that is the button's class, and a wrapper
       sharing it took the button's fixed position and swallowed the click. */
    <div className={`create create--${variant}`}>
      <button ref={button} type="button"
              className={variant === 'fab' ? `create-fab${away && !open ? ' is-away' : ''}` : 'btn btn-primary create-button'}
              aria-haspopup="menu" aria-expanded={open}
              aria-label={variant === 'fab' ? 'New' : undefined}
              title="Add something new"
              onClick={() => setOpen(o => !o)}>
        <Icon name="plus" size={variant === 'fab' ? 24 : 18} />
        {variant === 'sidebar' && <span className="tab-label">New</span>}
      </button>

      {open && (
        <div ref={menu} className="create-menu" role="menu" aria-label="Add something new"
             onKeyDown={onKeyDown}>
          {ITEMS.map(item => (
            <button key={item.key} type="button" role="menuitem" className="create-item"
                    onClick={() => choose(item.key)}>
              <span className="item-icon" aria-hidden="true"><Icon name={item.icon} size={17} /></span>
              <span className="create-text">
                <strong>{item.label}</strong>
                <span className="muted small">{item.hint}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {asking && <AskAmon onClose={() => setAsking(false)} />}

      {placing && (
        <AddToCalendar
          date={date}
          onClose={() => setPlacing(false)}
          onAdded={(what, repeat) => {
            for (const key of ['plan', 'week', 'month', 'year', 'rituals', 'occasions'])
              void qc.invalidateQueries({ queryKey: [key] });
            toast.say(
              repeat === 'weekly' ? `“${what}” is held every week now.`
                : repeat === 'yearly' ? `“${what}” comes round every year now.`
                : `“${what}” is on ${friendlyDate(date)}.`, 'good');
          }} />
      )}
    </div>
  );
}
